// askSmart-ui/src/hooks/useModuleBranding.ts
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { brandForPath } from "../moduleBranding";

/**
 * Keeps the browser tab — its icon AND its text — matching the module in view.
 *
 * Called ONCE, from App. index.html can only state what the app starts with;
 * every navigation after that is client-side, so the tab would otherwise keep
 * whatever the first page load happened to set. This re-applies on each pathname
 * change, which covers going into a module, moving between modules, and coming
 * back out to AskSmart — and a direct load or refresh of a module URL, since the
 * effect runs on mount as well.
 *
 * Both come from one `brandForPath` lookup, so the icon and the title can never
 * name two different modules.
 */
export function useModuleBranding(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const brand = brandForPath(pathname);

    if (document.title !== brand.title) document.title = brand.title;

    // The <link> in index.html is REUSED rather than replaced. Appending a second
    // one leaves both in the document, and which a browser honours is not worth
    // depending on — Safari keeps the first. One tag, href rewritten.
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }

    // The path carries spaces ("/CaddieGames Icon.png"), so it is encoded rather
    // than assigned raw. Compared before writing: assigning the same href still
    // makes some browsers re-fetch the image, which flickers the tab on every
    // navigation within one module.
    const encoded = encodeURI(brand.favicon);
    if (link.getAttribute("href") !== encoded) {
      link.setAttribute("href", encoded);
      // Kept in step with the file actually being served. A .jpg under
      // type="image/png" is ignored by strict browsers.
      link.setAttribute(
        "type",
        brand.favicon.toLowerCase().endsWith(".jpg") ? "image/jpeg" : "image/png",
      );
    }
  }, [pathname]);
}
